import { supabase } from '../lib/supabase';
import { generateSlug } from '../utils/slug';
import type { Incident, IncidentCreate, MapBounds } from '../types';
import { logger } from '../lib/logger';

const FETCH_LIMIT = 50;

export async function fetchIncidents(bounds: MapBounds): Promise<Incident[]> {
  try {
    logger.performance.start('fetchIncidents');

    // Make sure we have valid bounds
    if (!bounds || !bounds.north || !bounds.south || !bounds.east || !bounds.west) {
      logger.error(new Error('Invalid bounds provided'), 'fetchIncidents');
      return [];
    }
    
    // Log the raw bounds before normalization
    logger.info('Raw bounds', 'fetchIncidents', { 
      extra: { bounds }
    });
    
    const params = {
      limit_count: FETCH_LIMIT,
      max_lat: Math.min(Math.max(bounds.north, -90), 90),
      min_lat: Math.min(Math.max(bounds.south, -90), 90),
      max_lng: Math.min(Math.max(bounds.east, -180), 180),
      min_lng: Math.min(Math.max(bounds.west, -180), 180)
    };

    // Log normalized params
    logger.info('Normalized params', 'fetchIncidents', { 
      extra: { 
        params,
        boundingBox: {
          northEast: [params.max_lat, params.max_lng],
          southWest: [params.min_lat, params.min_lng]
        }
      }
    });
    const { data, error } = await supabase
      .rpc('get_incidents_in_bounds', params, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    // const { data, error } = await supabase
    //   .rpc('get_incidents_in_bounds', params);

    if (error) {
      logger.error(error, 'fetchIncidents_rpc_error');
      return [];
    }

    const incidents = (data || []) as Incident[];
    
    // Log more details about results
    logger.info('Fetch results', 'fetchIncidents', {
      extra: { 
        totalIncidents: incidents.length,
        firstIncident: incidents[0] ? {
          id: incidents[0].id,
          created_at: incidents[0].created_at,
          coordinates: incidents[0].location.coordinates
        } : null,
        lastIncident: incidents[incidents.length - 1] ? {
          id: incidents[incidents.length - 1].id,
          created_at: incidents[incidents.length - 1].created_at,
          coordinates: incidents[incidents.length - 1].location.coordinates
        } : null
      }
    });
    
    logger.performance.end('fetchIncidents');

    return incidents;
  } catch (error) {
    logger.error(error, 'fetchIncidents');
    return [];
  }
}

export async function createIncident(incident: IncidentCreate): Promise<Incident> {
  try {
    logger.performance.start('createIncident');
    logger.info('Creating incident', 'createIncident', { 
      extra: { type: incident.type, location: incident.location_zone }
    });

    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user) {
      throw new Error('User must be authenticated to create an incident');
    }

    const slug = generateSlug(incident.type, incident.location_zone);
    const point: { type: "Point"; coordinates: [number, number] } = {
      type: 'Point',
      coordinates: [incident.location.coordinates[0], incident.location.coordinates[1]]
    };

    const { data: incidentData, error: incidentError } = await supabase
      .from('incidents')
      .insert([{
        type: incident.type,
        description: incident.description,
        location: point,
        location_zone: incident.location_zone,
        severity: incident.severity,
        anonymous: incident.anonymous,
        verified: false,
        still_here_count: 0,
        no_longer_here_count: 0,
        reporter_id: session.session.user.id,
        created_at: new Date().toISOString(),
        slug
      }])
      .select()
      .single();

    if (incidentError || !incidentData) {
      logger.error(incidentError, 'createIncident');
      throw incidentError;
    }

    if (incident.media?.length) {
      const mediaPromises = incident.media.map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${incidentData.id}/${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('incident-media')
          .upload(fileName, file);

        if (uploadError) {
          logger.error(uploadError, 'mediaUpload');
          throw uploadError;
        }

        const { data: mediaUrl } = supabase.storage
          .from('incident-media')
          .getPublicUrl(fileName);

        const { error: mediaError } = await supabase
          .from('incident_media')
          .insert([{
            incident_id: incidentData.id,
            url: mediaUrl.publicUrl,
            type: file.type.startsWith('image/') ? 'image' : 'video'
          }]);

        if (mediaError) {
          logger.error(mediaError, 'mediaRecord');
          throw mediaError;
        }
      });

      await Promise.all(mediaPromises);
    }

    const { data: finalIncident, error: fetchError } = await supabase
      .from('incidents')
      .select(`
        *,
        incident_media (
          id,
          type,
          url
        )
      `)
      .eq('id', incidentData.id)
      .single();

    if (fetchError || !finalIncident) {
      logger.error(fetchError, 'fetchFinalIncident');
      throw fetchError;
    }

    logger.performance.end('createIncident');
    return finalIncident as Incident;
  } catch (error) {
    logger.error(error, 'createIncident');
    throw error;
  }
}

export async function updateIncidentPresence(
  id: string,
  type: 'still-here' | 'no-longer-here'
): Promise<void> {
  try {
    logger.performance.start('updateIncidentPresence');
    logger.info('Updating incident presence', 'updateIncidentPresence', { 
      extra: { id, type }
    });

    const column = type === 'still-here' ? 'still_here_count' : 'no_longer_here_count';
    
    const { data: currentIncident, error: fetchError } = await supabase
      .from('incidents')
      .select(column)
      .eq('id', id)
      .single();

    if (fetchError) {
      logger.error(fetchError, 'fetchCurrentCount');
      throw new Error(`Failed to fetch current count: ${fetchError.message}`);
    }

    const currentCount = currentIncident?.[column] || 0;
    
    const { error: updateError } = await supabase
      .from('incidents')
      .update({ [column]: currentCount + 1 })
      .eq('id', id);

    if (updateError) {
      logger.error(updateError, 'updatePresence');
      throw new Error(`Failed to update presence: ${updateError.message}`);
    }

    logger.performance.end('updateIncidentPresence');
  } catch (error) {
    logger.error(error, 'updateIncidentPresence');
    throw error;
  }
}

export async function fetchIncidentBySlug(slug: string): Promise<Incident | null> {
  try {
    const { data, error } = await supabase
      .from('incidents')
      .select(`
        *,
        incident_media (
          id,
          type,
          url
        )
      `)
      .eq('slug', slug)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      ...data,
      incident_media: data.incident_media || []
    } as Incident;
  } catch (error) {
    logger.error(error, 'fetchIncidentBySlug');
    return null;
  }
}

export async function fetchUserIncidents(): Promise<Incident[]> {
  try {
    logger.performance.start('fetchUserIncidents');
    
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user) {
      throw new Error('User must be authenticated to fetch their incidents');
    }
    
    const userId = session.session.user.id;
    
    const { data, error } = await supabase
      .from('incidents')
      .select(`
        *,
        incident_media (
          id,
          url,
          type
        )
      `)
      .eq('reporter_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      logger.error(error, 'fetchUserIncidents');
      throw error;
    }
    
    logger.performance.end('fetchUserIncidents');
    return data || [];
  } catch (error) {
    logger.error(error, 'fetchUserIncidents');
    return [];
  }
}

export async function deleteIncident(incidentId: string): Promise<boolean> {
  try {
    logger.performance.start('deleteIncident');
    
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user) {
      throw new Error('User must be authenticated to delete their incidents');
    }
    
    const userId = session.session.user.id;
    
    // First verify this incident belongs to the user
    const { data: incident, error: fetchError } = await supabase
      .from('incidents')
      .select('id, reporter_id')
      .eq('id', incidentId)
      .single();
    
    if (fetchError) {
      logger.error(fetchError, 'deleteIncident_fetch');
      throw fetchError;
    }
    
    if (!incident || incident.reporter_id !== userId) {
      throw new Error('Not authorized to delete this incident');
    }
    
    // Delete the incident
    const { error: deleteError } = await supabase
      .from('incidents')
      .delete()
      .eq('id', incidentId);
    
    if (deleteError) {
      logger.error(deleteError, 'deleteIncident_delete');
      throw deleteError;
    }
    
    logger.performance.end('deleteIncident');
    return true;
  } catch (error) {
    logger.error(error, 'deleteIncident');
    return false;
  }
}